import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import {
  CurecallPasswordInput,
  CurecallButton,
  ECurecallNotificationAppearance,
  Strength,
} from '@honestica/curecall-ui'
import { UserService } from 'src/services/user.service'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'

type TPasswordFormValues = {
  currentPassword: string
  password: string
  passwordConfirmation: string
}

const AuthForm = () => {
  const { notify } = useNotification()
  const { t } = useTranslation()
  const methods = useForm<TPasswordFormValues>()
  const {
    formState: { errors },
    register,
    setValue,
    setError,
    handleSubmit,
  } = methods

  const [passwordStrength, setPasswordStrength] = useState<Strength>()

  const onAuthFormSubmit = async (values: TPasswordFormValues) => {
    const { currentPassword, password, passwordConfirmation } = values

    if (passwordStrength !== 'STRONG') {
      setError('password', { message: t('pages.auth.input.error') })
      return
    }
    if (password !== passwordConfirmation) {
      setError('passwordConfirmation', { message: t('pages.auth.input.passwords-are-not-identical') })
      return
    }
    try {
      await UserService.updateMe({ currentPassword, password })
      notify(
        {
          title: t('pages.auth.notification.password-updated-title'),
          message: t('pages.auth.notification.password-updated-message'),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        },
        13000,
      )
    } catch {
      notify(
        {
          title: t('pages.auth.error.server'),
          message: t('pages.auth.error.password-updated-failure'),
          appearance: ECurecallNotificationAppearance.WARNING,
        },
        13000,
      )
    }
  }

  return (
    <div className="c-border c-border-grey-300 c-rounded-md c-w-1/2 c-p-3 c-mb-5 c-w-full">
      <h4 className="c-mb-4">{t('pages.auth.description.reset-password')}</h4>

      <FormProvider {...methods}>
        <form
          action="#"
          method="POST"
          onSubmit={handleSubmit(onAuthFormSubmit)}
          className="c-w-full c-flex c-flex-col c-gap-6 [&>*]:c-w-full"
        >
          <CurecallPasswordInput
            {...register('currentPassword', {
              required: t(`pages.auth.input.password-validation.no-password`),
            })}
            label={t('pages.auth.input.old-password')}
            isRequired={true}
            errorMessage={errors?.currentPassword?.message}
            onChange={(event) => setValue('currentPassword', event.target.value.trim())}
          />
          <CurecallPasswordInput
            {...register('password', { required: t(`pages.auth.input.password-validation.no-password`) })}
            label={t('pages.auth.input.new-password')}
            isRequired={true}
            errorMessage={errors?.password?.message}
            strengthBarLabels={{
              EMPTY: t('pages.auth.input.empty'),
              WEAK: t('pages.auth.input.weak'),
              MEDIUM: t('pages.auth.input.medium'),
              STRONG: t('pages.auth.input.strong'),
            }}
            onPasswordStrengthChange={(strength) => setPasswordStrength(strength)}
            onChange={(event) => setValue('password', event.target.value.trim())}
          />
          <CurecallPasswordInput
            {...register('passwordConfirmation', {
              required: t(`pages.auth.input.password-validation.no-password`),
            })}
            isRequired={true}
            label={t('pages.auth.input.confirm-password')}
            errorMessage={errors?.passwordConfirmation?.message}
            onChange={(event) => setValue('passwordConfirmation', event.target.value.trim())}
          />

          <CurecallButton className="c-mt-6" type="submit">
            {t('pages.auth.cta.reinitialize-my-password')}
          </CurecallButton>
        </form>
      </FormProvider>
    </div>
  )
}

export { AuthForm }
